import { useTeams } from '@/hooks/useTeams';
import { useMultiTeam } from '@/hooks/useMultiTeam';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Users } from 'lucide-react';

interface TeamSelectorProps {
  value?: string;
  onValueChange: (teamId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  showLabel?: boolean;
}

/**
 * Team selector dropdown. Only visible when multi-team is enabled.
 * Auto-selects the default team if no value is provided.
 */
export function TeamSelector({
  value,
  onValueChange,
  disabled = false,
  placeholder = 'Select team',
  showLabel = false,
}: TeamSelectorProps) {
  const { isMultiTeamEnabled } = useMultiTeam();
  const { teams, isLoading } = useTeams();

  // Don't render if multi-team is not enabled
  if (!isMultiTeamEnabled) {
    return null;
  }

  const activeTeams = teams.filter((t) => t.is_active);
  const defaultTeam = activeTeams.find((t) => t.is_default);

  // Auto-select default team if no value
  const selectedValue = value || defaultTeam?.id || '';

  return (
    <div className="flex items-center gap-2">
      {showLabel && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Users className="h-4 w-4" />
          <span>Team:</span>
        </div>
      )}
      <Select
        value={selectedValue}
        onValueChange={onValueChange}
        disabled={disabled || isLoading}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder={isLoading ? 'Loading...' : placeholder} />
        </SelectTrigger>
        <SelectContent>
          {activeTeams.map((team) => (
            <SelectItem key={team.id} value={team.id}>
              <div className="flex items-center gap-2">
                <span>{team.name}</span>
                {team.is_default && (
                  <Badge variant="secondary" className="text-xs">
                    Default
                  </Badge>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
